import type { TriageLevel } from '@/types';
import { isGerman } from '@/i18n/locale';
import { Icon } from '@/components/ui/icons';

interface Props {
  level: TriageLevel;
}

type Tone = 'er' | 'urgent' | 'call' | 'home';

interface Copy {
  title: string;
  subtitle: string;
  tone: Tone;
}

// Emergency number follows the UI language: the app's DACH users reach
// emergency services on 112, not 911. Same rule as <Disclaimer />.
function copyFor(level: TriageLevel, de: boolean): Copy {
  const COPY_DE: Record<TriageLevel, Copy> = {
    er: {
      title: 'Das könnte ein Notfall sein',
      subtitle: 'Ruf jetzt die 112 oder fahre sofort in die nächste Notaufnahme.',
      tone: 'er',
    },
    urgent: {
      title: 'Heute noch ärztlich abklären lassen',
      subtitle: 'Das sollte innerhalb von Stunden angeschaut werden, nicht erst in Tagen.',
      tone: 'urgent',
    },
    call_ped: {
      title: 'Ein Anruf in der Kinderarztpraxis lohnt sich',
      subtitle: 'Wenn es schlimmer wird, warte nicht bis morgen — die meisten Praxen haben eine Telefonsprechstunde.',
      tone: 'call',
    },
    home: {
      title: 'Zuhause gut zu begleiten',
      subtitle: 'Achte auf die Warnzeichen, die Alma oben genannt hat.',
      tone: 'home',
    },
  };
  const COPY_EN: Record<TriageLevel, Copy> = {
    er: {
      title: 'This may need emergency care',
      // The banner doesn't know the parent's country; the hard-coded reply
      // above it does and names the exact number.
      subtitle: 'Call your local emergency number (112 in Europe, 911 in the US) or go to the nearest ER right now.',
      tone: 'er',
    },
    urgent: {
      title: 'Consider urgent care',
      subtitle: 'A clinician should evaluate this within hours, not days.',
      tone: 'urgent',
    },
    call_ped: {
      title: 'Worth a call to your pediatrician',
      subtitle: "Most offices have a nurse line — don't wait until tomorrow if it's worsening.",
      tone: 'call',
    },
    home: {
      title: 'Likely manageable at home',
      subtitle: 'Watch for the signs Alma listed above.',
      tone: 'home',
    },
  };
  return (de ? COPY_DE : COPY_EN)[level];
}

const PALETTE: Record<Tone, string> = {
  er: 'bg-danger/10 border-danger/40 text-danger-700',
  urgent: 'bg-warning/15 border-warning/50 text-warning-700',
  call: 'bg-primary/10 border-primary-200 text-primary-700',
  home: 'bg-primary-50 border-primary-100 text-primary-700',
};

export function TriageBanner({ level }: Props) {
  const copy = copyFor(level, isGerman());
  const isEr = copy.tone === 'er';
  return (
    <div
      role="alert"
      aria-live={isEr ? 'assertive' : 'polite'}
      className={`mx-auto w-full max-w-chat px-4 pt-3 shrink-0`}
    >
      <div className={`flex gap-3 rounded-xl border px-4 py-3 ${PALETTE[copy.tone]}`}>
        <span className="shrink-0 mt-0.5" aria-hidden>
          {isEr ? <Icon.AlertTriangle size={20} /> : <Icon.Info size={20} />}
        </span>
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm">{copy.title}</div>
          <div className="text-sm mt-0.5 opacity-90">{copy.subtitle}</div>
        </div>
      </div>
    </div>
  );
}
